import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { ServiceAttributeDefinition, ServiceCategory, ServiceOffering } from '../types/Service';
import { archiveService, getServiceById, getServiceCategories, removeServiceAttachment, serviceCatalogService, setServicePrimaryImage, updateService, updateServiceStatus, uploadServiceAttachment } from '../services/serviceCatalogService';
import { CategorySelect } from '../../catalog/CategorySelect';
import { CatalogAttributeFields, formatAttributeValue } from '../../catalog/CatalogAttributeFields';
import { CatalogMediaPanel } from '../../catalog/CatalogMediaPanel';
import { formatMoney } from '../../invoices/invoiceForm';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

const parseNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatAmount = (value?: number) => (value === undefined ? '-' : String(value));

export const ServiceDetails: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const currency = useTenantCurrency();
  const [item, setItem] = React.useState<ServiceOffering | null>(null);
  const [categories, setCategories] = React.useState<ServiceCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: '',
    description: '',
    categoryId: '',
    code: '',
    costPrice: '',
    markupPercent: '',
    salePrice: '',
    taxPercent: '',
    durationMinutes: '',
    accountingCode: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [definitions, setDefinitions] = React.useState<ServiceAttributeDefinition[]>([]);
  const [attributes, setAttributes] = React.useState<Record<string, string>>({});
  const [uploadingMedia, setUploadingMedia] = React.useState(false);
  const canUpdate = hasPermission(PermissionScopes.servicesUpdate);
  const canDelete = hasPermission(PermissionScopes.servicesDelete);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [service, serviceCategories] = await Promise.all([
          id?.trim() ? getServiceById(id) : Promise.resolve(null),
          getServiceCategories(),
        ]);
        setItem(service);
        setCategories(serviceCategories);
        if (service) {
          setForm({
            name: service.name,
            description: service.description || '',
            categoryId: service.categoryId,
            code: service.code || '',
            costPrice: service.costPrice === undefined ? '' : String(service.costPrice),
            markupPercent: service.markupPercent === undefined ? '' : String(service.markupPercent),
            salePrice: service.salePrice === undefined ? '' : String(service.salePrice),
            taxPercent: service.taxPercent === undefined ? '' : String(service.taxPercent),
            durationMinutes: service.durationMinutes === undefined ? '' : String(service.durationMinutes),
            accountingCode: service.accountingCode || '',
          });
          setAttributes(service.attributes);
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  const categoryIdForFields = editMode ? form.categoryId : item?.categoryId;

  React.useEffect(() => {
    if (!categoryIdForFields) {
      setDefinitions([]);
      return;
    }

    let cancelled = false;
    void serviceCatalogService.getAttributes(categoryIdForFields, true)
      .then(items => {
        if (cancelled) return;
        setDefinitions(items);
        setAttributes(prev => {
          const next: Record<string, string> = {};
          items.forEach(definition => {
            if (prev[definition.id]) next[definition.id] = prev[definition.id];
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryIdForFields]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'services' });
    if (!form.categoryId) nextErrors.categoryId = t('categoryRequired', { ns: 'services' });
    definitions.forEach(definition => {
      if (definition.isRequired && !(attributes[definition.id] || '').trim()) {
        nextErrors[definition.id] = t('attributeRequired', { ns: 'services', name: definition.name });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSave = async () => {
    if (!id || !validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await updateService(id, {
        name: form.name.trim(),
        description: form.description.trim(),
        categoryId: form.categoryId,
        code: form.code.trim() || undefined,
        clearCode: !form.code.trim(),
        costPrice: parseNumber(form.costPrice),
        clearCostPrice: !form.costPrice.trim(),
        markupPercent: parseNumber(form.markupPercent),
        clearMarkupPercent: !form.markupPercent.trim(),
        salePrice: parseNumber(form.salePrice),
        clearSalePrice: !form.salePrice.trim(),
        taxPercent: parseNumber(form.taxPercent),
        clearTaxPercent: !form.taxPercent.trim(),
        durationMinutes: parseNumber(form.durationMinutes),
        clearDurationMinutes: !form.durationMinutes.trim(),
        accountingCode: form.accountingCode.trim() || undefined,
        clearAccountingCode: !form.accountingCode.trim(),
        attributes: Object.fromEntries(definitions.map(definition => [definition.id, attributes[definition.id]?.trim() || null])),
      });
      if (!updated) {
        setGeneralError(t('failedToSave', { ns: 'services' }));
        return;
      }
      setItem(updated);
      setAttributes(updated.attributes);
      setEditMode(false);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'services' }));
    } finally {
      setSaving(false);
    }
  };

  const onArchiveOrRestore = async () => {
    if (!id || !item) return;
    setSaving(true);
    setGeneralError(null);
    try {
      if (item.status === 'archived') {
        const updated = await updateServiceStatus(id, 'active');
        if (updated) setItem(updated);
      } else {
        await archiveService(id);
        const updated = await getServiceById(id);
        if (updated) setItem(updated);
      }
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToArchive', { ns: 'services' }));
    } finally {
      setSaving(false);
    }
  };

  const applyMediaUpdate = (updated: ServiceOffering | null, fallback: string) => {
    if (!updated) {
      setGeneralError(fallback);
      return;
    }
    setItem(updated);
  };

  const onUploadAttachment = async (file: File, setAsPrimary: boolean) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await uploadServiceAttachment(id, file, setAsPrimary);
      applyMediaUpdate(updated, t('failedToUploadAttachment', { ns: 'services' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToUploadAttachment', { ns: 'services' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onRemoveAttachment = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await removeServiceAttachment(id, attachmentId);
      applyMediaUpdate(updated, t('failedToRemoveAttachment', { ns: 'services' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToRemoveAttachment', { ns: 'services' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onSetPrimaryImage = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await setServicePrimaryImage(id, attachmentId);
      applyMediaUpdate(updated, t('failedToSetDefaultPhoto', { ns: 'services' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSetDefaultPhoto', { ns: 'services' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingItem', { ns: 'services' })} /></div>;
  }

  if (!item) {
    return <div>{t('notFound', { ns: 'services' })}</div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'services' }), path: '/services' },
        { label: item.name, path: `/services/${item.id}` },
      ]} />
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{item.name}</h2>
        <div className={styles.headerActions}>
          {canDelete ? (
            <button type="button" className="btn btn-secondary" onClick={onArchiveOrRestore} disabled={saving}>
              {item.status === 'archived' ? t('restore', { ns: 'services' }) : t('archive', { ns: 'services' })}
            </button>
          ) : null}
          {canUpdate && !editMode ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setAttributes(item.attributes);
                setEditMode(true);
              }}
            >
              {t('edit', { ns: 'common' })}
            </button>
          ) : null}
          {editMode ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setAttributes(item.attributes);
                  setEditMode(false);
                }}
                disabled={saving}
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>{t('save', { ns: 'services' })}</button>
            </>
          ) : null}
        </div>
      </div>
      {generalError ? <div className="error">{generalError}</div> : null}

      <CatalogMediaPanel
        ns="services"
        itemName={item.name}
        attachments={item.attachments}
        canUpdate={canUpdate}
        busy={saving || uploadingMedia}
        onUpload={onUploadAttachment}
        onRemove={onRemoveAttachment}
        onSetPrimary={onSetPrimaryImage}
      />

      {editMode ? (
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>{t('name', { ns: 'services' })}</label>
            <input className={styles.input} value={form.name} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
            {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('category', { ns: 'services' })}</label>
            <CategorySelect
              className={styles.input}
              categories={categories}
              value={form.categoryId}
              onChange={categoryId => setForm(prev => ({ ...prev, categoryId }))}
            />
            <div className={styles.fieldHint}>{t('categoryHint', { ns: 'services' })}</div>
          </div>
          <CatalogAttributeFields
            ns="services"
            definitions={definitions}
            values={attributes}
            errors={errors}
            onChange={(attributeId, value) => setAttributes(prev => ({ ...prev, [attributeId]: value }))}
          />
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label className={styles.label}>{t('description', { ns: 'services' })}</label>
            <textarea className={styles.textarea} value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
            <div className={styles.fieldHint}>{t('descriptionHint', { ns: 'services' })}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('code', { ns: 'services' })}</label>
            <input className={styles.input} value={form.code} onChange={event => setForm(prev => ({ ...prev, code: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('accountingCode', { ns: 'services' })}</label>
            <input className={styles.input} value={form.accountingCode} onChange={event => setForm(prev => ({ ...prev, accountingCode: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('durationMinutes', { ns: 'services' })}</label>
            <input className={styles.input} type="number" min="0" step="1" value={form.durationMinutes} onChange={event => setForm(prev => ({ ...prev, durationMinutes: event.target.value }))} />
            <div className={styles.fieldHint}>{t('durationHint', { ns: 'services' })}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('costPrice', { ns: 'services' })} ({currency})</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.costPrice} onChange={event => setForm(prev => ({ ...prev, costPrice: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('markupPercent', { ns: 'services' })}</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.markupPercent} onChange={event => setForm(prev => ({ ...prev, markupPercent: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('salePrice', { ns: 'services' })} ({currency})</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.salePrice} onChange={event => setForm(prev => ({ ...prev, salePrice: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('taxPercent', { ns: 'services' })}</label>
            <input className={styles.input} type="number" min="0" max="100" step="0.01" value={form.taxPercent} onChange={event => setForm(prev => ({ ...prev, taxPercent: event.target.value }))} />
          </div>
        </div>
      ) : (
        <div className={styles.detailsGrid}>
          <div>
            <p className={styles.detailLabel}>{t('category', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.categoryName || '-'}</p>
          </div>
          {definitions.map(definition => (
            <div key={definition.id}>
              <p className={styles.detailLabel}>{definition.name}</p>
              <p className={styles.detailValue}>
                {formatAttributeValue(
                  definition,
                  item.attributes[definition.id],
                  t('yes', { ns: 'services' }),
                  t('no', { ns: 'services' }),
                )}
              </p>
            </div>
          ))}
          <div className={styles.detailItemWide}>
            <p className={styles.detailLabel}>{t('description', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.description || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('code', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.code || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('accountingCode', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.accountingCode || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('durationMinutes', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.durationMinutes ?? '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('costPrice', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{formatMoney(item.costPrice, currency)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('markupPercent', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{formatAmount(item.markupPercent)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('salePrice', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{formatMoney(item.salePrice, currency)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('taxPercent', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{formatAmount(item.taxPercent)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('status', { ns: 'services' })}</p>
            <p className={styles.detailValue}>{item.status === 'archived' ? t('statusArchived', { ns: 'services' }) : t('statusActive', { ns: 'services' })}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetails;
